const MONTHS_RU_GEN = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

/**
 * «7 авг 2026» — day, genitive month, year in local time. Invalid input renders as an empty string.
 * A date-only value ('2026-06-20') is a calendar date: parsed as local midnight, not UTC — otherwise
 * it renders the previous day west of UTC.
 */
export function formatShortDate(iso: string | Date): string {
  const date = typeof iso === 'string' ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00` : iso) : iso;
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getDate()} ${MONTHS_RU_GEN[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * «20.06.2026» — the table date column. Same calendar-date parsing as `formatShortDate`: a date-only
 * value is local midnight, not UTC, or it renders the previous day west of Greenwich.
 */
export function formatDotDate(iso: string): string {
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00` : iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('ru-RU');
}

const SIZE_UNITS = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];

/** «1,2 ГБ» / «340 МБ» — binary units, one decimal under 10; empty for 0/undefined. */
export function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit++;
  }

  const rounded = unit === 0 || value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${SIZE_UNITS[unit]}`;
}

export const GALLERY_FORMS = ['галерея', 'галереи', 'галерей'] as const;

/** Russian plural pick — `forms` is [1 разворот, 2 разворота, 5 разворотов]. */
export function pluralRu(n: number, forms: readonly [string, string, string]): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
}

/** «1 234 фото» — digits grouped by a narrow no-break space, word form picked by `pluralRu`. */
export function formatCount(n: number, forms: readonly [string, string, string]): string {
  const grouped = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped} ${pluralRu(n, forms)}`;
}

const RUB = new Intl.NumberFormat('ru-RU');

/** «3 900 ₽» — ru-RU digit groups; a no-break space keeps «₽» on its number's line. */
export function rub(n: number): string {
  return `${RUB.format(n)}\xa0₽`;
}

/** «45 сек» → «1 мин 05 сек» → «2 ч 03 мин»; anything negative or non-finite reads as «0 сек». */
export function formatDuration(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  if (total < 60) return `${total} сек`;

  const pad = (n: number) => String(n).padStart(2, '0');
  if (total < 3600) return `${Math.floor(total / 60)} мин ${pad(total % 60)} сек`;
  return `${Math.floor(total / 3600)} ч ${pad(Math.floor((total % 3600) / 60))} мин`;
}

/** Whole days between `iso` and `now`, rounded. */
export function daysSince(iso: string, now: Date = new Date()): number {
  return Math.round((now.getTime() - new Date(iso).getTime()) / 86_400_000);
}

const DAY_FORMS = ['день', 'дня', 'дней'] as const;
const WEEK_FORMS = ['неделя', 'недели', 'недель'] as const;
const MONTH_FORMS = ['месяц', 'месяца', 'месяцев'] as const;
const YEAR_FORMS = ['год', 'года', 'лет'] as const;

/**
 * Age of a moment, no «назад» suffix: сегодня → days (≤30d) → weeks (until 12 weeks) → months
 * (until 24 months) → years. Each unit reads plainly, e.g. «3 недели», «5 месяцев».
 */
export function relativeAge(iso: string, now: Date = new Date()): string {
  const days = daysSince(iso, now);
  if (days <= 0) return 'сегодня';
  if (days <= 30) return `${days} ${pluralRu(days, DAY_FORMS)}`;

  const weeks = Math.round(days / 7);
  if (weeks <= 12) return `${weeks} ${pluralRu(weeks, WEEK_FORMS)}`;

  const months = Math.round(days / 30);
  if (months < 24) return `${months} ${pluralRu(months, MONTH_FORMS)}`;

  const years = Math.round(days / 365);
  return `${years} ${pluralRu(years, YEAR_FORMS)}`;
}

type Gender = 'm' | 'f';
type Declined = { gen: string; gender?: Gender };

/** Male first names that drop a vowel in oblique cases. */
const FLEETING_FIRST: Record<string, string> = { павел: 'Павла', лев: 'Льва', пётр: 'Петра', петр: 'Петра' };

function declineSurnamePart(w: string): Declined | null {
  const l = w.toLowerCase();
  if (/(ова|ева|ёва|ина|ына)$/.test(l)) return { gen: `${w.slice(0, -1)}ой`, gender: 'f' };
  if (/ая$/.test(l)) return { gen: `${w.slice(0, -2)}ой`, gender: 'f' };
  if (/(ов|ев|ёв|ин|ын)$/.test(l)) return { gen: `${w}а`, gender: 'm' };
  // Толстой → Толстого; a short Цой is not adjectival and falls through to unknown.
  if (/(ый|[гкх]ий|..[^аеёиоуыэюя]ой)$/.test(l)) return { gen: `${w.slice(0, -2)}ого`, gender: 'm' };
  if (/ий$/.test(l)) return { gen: `${w.slice(0, -2)}его`, gender: 'm' };
  // Шевченко, Черных: same form in every case, gender unknown.
  if (/([оеэиую]|их|ых)$/.test(l)) return { gen: w };
  return null;
}

function declineSurname(w: string): Declined | null {
  const parts = w.split('-').map(declineSurnamePart);
  if (parts.some(p => !p)) return null;
  const genders = new Set(parts.map(p => p?.gender).filter(Boolean));
  if (genders.size > 1) return null;
  return { gen: parts.map(p => p?.gen).join('-'), gender: [...genders][0] };
}

function declineFirstName(w: string, gender: Gender | undefined): string | null {
  const l = w.toLowerCase();
  if (l.includes('-')) return null;
  if (FLEETING_FIRST[l]) return gender === 'f' ? null : FLEETING_FIRST[l];
  if (/я$/.test(l)) return `${w.slice(0, -1)}и`;
  if (/[гкхжшчщ]а$/.test(l)) return `${w.slice(0, -1)}и`;
  if (/а$/.test(l)) return `${w.slice(0, -1)}ы`;
  if (/й$/.test(l)) return `${w.slice(0, -1)}я`;
  if (/ь$/.test(l)) return gender ? `${w.slice(0, -1)}${gender === 'm' ? 'я' : 'и'}` : null;
  // A consonant ending declines for men only (Кармен stays Кармен); no gendered surname = unknown.
  if (/[бвгджзклмнпрстфхцчшщ]$/.test(l)) return gender === 'm' ? `${w}а` : gender === 'f' ? w : null;
  if (/[оеэиую]$/.test(l)) return w;
  return null;
}

/**
 * «Жанна Точкина» → «Жанны Точкиной» — genitive of «Имя Фамилия». Any form it cannot derive with
 * certainty (one word, three words, unknown ending, unreadable gender) returns `fullName` unchanged,
 * so a caller detects «not declined» by equality and never prints a wrong form.
 */
export function genitiveFullName(fullName: string): string {
  const words = fullName.trim().split(/\s+/);
  if (words.length !== 2) return fullName;
  const surname = declineSurname(words[1]);
  const first = surname && declineFirstName(words[0], surname.gender);
  return surname && first ? `${first} ${surname.gen}` : fullName;
}

/**
 * «Алина Андронова» → «Алину Андронову» — accusative of «Имя Фамилия». A man's form equals the genitive;
 * a woman's needs a gendered surname. Anything uncertain returns `fullName` unchanged, as the genitive does.
 */
export function accusativeFullName(fullName: string): string {
  const words = fullName.trim().split(/\s+/);
  const surname = words.length === 2 ? declineSurname(words[1]) : null;
  if (!surname?.gender) return fullName;
  const [first, last] = words;
  const l = first.toLowerCase();
  // -а/-я first names take -у/-ю in both genders (Никиту, Алину).
  const ending = /а$/.test(l) ? 'у' : /я$/.test(l) ? 'ю' : null;
  if (surname.gender === 'm') {
    const genitive = genitiveFullName(fullName);
    if (genitive === fullName) return fullName;
    const [genFirst, genLast] = genitive.split(' ');
    return `${ending ? first.slice(0, -1) + ending : genFirst} ${genLast}`;
  }

  if (l.includes('-') || FLEETING_FIRST[l]) return fullName;
  const firstAcc = ending ? `${first.slice(0, -1)}${ending}` : first;
  const lastAcc = last
    .split('-')
    .map(part => (/ая$/i.test(part) ? `${part.slice(0, -2)}ую` : /а$/i.test(part) ? `${part.slice(0, -1)}у` : part))
    .join('-');
  return `${firstAcc} ${lastAcc}`;
}
